// AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { getDeviceId } from "../utils/deviceId";

const API = process.env.REACT_APP_API_URL;
const AuthContext = createContext(null);

// helpers storage (session, no persistente)
const ACCESS_TOKEN_KEY = "scenathon_access_token";
const REFRESH_TOKEN_KEY = "scenathon_refresh_token";
const CSRF_STORAGE_KEY = "scenathon_csrf_token";
const PENDING_TASK_KEY = "scenathon_pending_task_v1";

function readPendingTaskFromStorage() {
  try {
    const raw = localStorage.getItem(PENDING_TASK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const id = Number(parsed.id);
    if (!Number.isFinite(id) || id <= 0) return null;
    return {
      ...parsed,
      id,
    };
  } catch {
    return null;
  }
}

/** =========================
 * JWT helpers (client-side)
 * ========================= */
function getJwtExpMs(token) {
  try {
    const [, payloadB64] = token.split(".");
    if (!payloadB64) return 0;
    const payloadJson = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(payloadJson);
    return payload?.exp ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function shouldRefreshSoon(token, thresholdMs = 2 * 60 * 1000) {
  const expMs = getJwtExpMs(token);
  if (!expMs) return true;
  return expMs - Date.now() <= thresholdMs;
}

export function AuthProvider({ children }) {
  // ✅ Recupera access token del sessionStorage si existe
  const [accessToken, setAccessToken] = useState(() => {
    try {
      return sessionStorage.getItem(ACCESS_TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  // CSRF: guardado en sessionStorage
  const [csrfToken, setCsrfToken] = useState(() => {
    try {
      return sessionStorage.getItem(CSRF_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const [pendingTask, setPendingTask] = useState(() =>
    readPendingTaskFromStorage(),
  );
  const [pendingTaskState, setPendingTaskState] = useState("idle");

  // evita refresh simultáneos
  const refreshPromiseRef = useRef(null);
  // ref estable para logout (evita TDZ en useEffect definido antes del useCallback)
  const logoutRef = useRef(null);

  // ✅ Guarda access token en sessionStorage
  const saveAccessToken = useCallback((token) => {
    const newToken = token || null;
    setAccessToken(newToken);
    try {
      if (newToken) sessionStorage.setItem(ACCESS_TOKEN_KEY, newToken);
      else sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  // ✅ Guarda refresh token en sessionStorage (fallback si cookies bloqueadas)
  const saveRefreshToken = useCallback((token) => {
    try {
      if (token) sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
      else sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  const saveCsrf = useCallback((t) => {
    const token = t || null;
    setCsrfToken(token);
    try {
      if (token) sessionStorage.setItem(CSRF_STORAGE_KEY, token);
      else sessionStorage.removeItem(CSRF_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const savePendingTask = useCallback((task) => {
    setPendingTask(task || null);
    try {
      if (task) localStorage.setItem(PENDING_TASK_KEY, JSON.stringify(task));
      else localStorage.removeItem(PENDING_TASK_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  const clearPendingTask = useCallback(() => {
    savePendingTask(null);
    setPendingTaskState("idle");
  }, [savePendingTask]);

  const startPendingTask = useCallback(
    ({ id, name, country, colname, overwrite }) => {
      const taskId = Number(id);
      if (!Number.isFinite(taskId) || taskId <= 0) return;
      savePendingTask({
        id: taskId,
        name: name || "",
        country: country || "",
        colname: colname || "",
        overwrite: !!overwrite,
        startedAt: new Date().toISOString(),
      });
      setPendingTaskState("todo");
    },
    [savePendingTask],
  );

  /**
   * ✅ REFRESH:
   * - Usa cookie refresh (preferido)
   * - Si no hay cookie, usa token de sessionStorage (fallback para cookies bloqueadas)
   * - devuelve access token nuevo o null
   */
  const refresh = useCallback(async () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    refreshPromiseRef.current = (async () => {
      // Intenta refresh con cookie + sessionStorage como fallback
      const body = {};
      let refreshTokenFromStorage = null;
      
      // Si no hay cookie, intenta recuperar del sessionStorage
      try {
        refreshTokenFromStorage = sessionStorage.getItem(REFRESH_TOKEN_KEY);
      } catch {}
      
      if (refreshTokenFromStorage) {
        body.refreshToken = refreshTokenFromStorage;
      }

      const res = await fetch(`${API}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "y-device-id": getDeviceId(),
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        saveAccessToken(null);
        setUser(null);
        saveCsrf(null);
        saveRefreshToken(null);
        return null;
      }

      const data = await res.json().catch(() => ({}));
      const token = data.token || null;

      saveAccessToken(token); // ✅ Guarda en sessionStorage
      setUser(data.user || null);

      // ✅ si backend devuelve refreshToken, guardarlo en sessionStorage
      if (data.refreshToken) saveRefreshToken(data.refreshToken);

      // ✅ si backend devuelve csrfToken, guardarlo
      if (data.csrfToken) saveCsrf(data.csrfToken);

      return token;
    })();

    try {
      return await refreshPromiseRef.current;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [saveAccessToken, saveRefreshToken, saveCsrf]);

  // ✅ Bootstrapping: al montar intenta refresh con cookie HttpOnly
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        await refresh();
      } catch {
        // silencioso
      } finally {
        if (alive) setBootstrapping(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [refresh]);

  // ✅ Sliding expiration + auto-logout por inactividad
  useEffect(() => {
    if (bootstrapping) return;

    // ─── Configuración ───────────────────────────────────────────────
    const IDLE_TIMEOUT_MS = 30 * 60_000; // 30 min sin actividad → logout
    const ACTIVE_WINDOW   = IDLE_TIMEOUT_MS; // ventana de actividad = misma que idle
    const THRESHOLD       = 2 * 60_000;  // refrescar si el token expira en <= 2 min
    // ────────────────────────────────────────────────────────────────

    let lastActivity = Date.now();
    let mounted = true;
    let idleTimer = null;

    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (mounted) logoutRef.current?.();
      }, IDLE_TIMEOUT_MS);
    };

    const markActivity = () => {
      lastActivity = Date.now();
      resetIdleTimer();
    };

    const maybeRefresh = async () => {
      if (!mounted) return;
      if (document.visibilityState !== "visible") return;

      // si no hay access token, intenta revivir sesión con refresh cookie
      if (!accessToken) {
        await refresh();
        return;
      }

      const idleMs = Date.now() - lastActivity;

      if (idleMs <= ACTIVE_WINDOW && shouldRefreshSoon(accessToken, THRESHOLD)) {
        await refresh();
      }
    };

    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
    events.forEach((ev) =>
      window.addEventListener(ev, markActivity, { passive: true }),
    );

    const onFocus = () => {
      void maybeRefresh();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") void maybeRefresh();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    const interval = setInterval(() => {
      void maybeRefresh();
    }, 30_000);

    // Arranca el timer de inactividad desde el montaje
    resetIdleTimer();

    return () => {
      mounted = false;
      clearInterval(interval);
      if (idleTimer) clearTimeout(idleTimer);

      events.forEach((ev) => window.removeEventListener(ev, markActivity));
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [bootstrapping, accessToken, refresh]);

  /**
   * ✅ login: setea token, user, y guarda csrf en sessionStorage
   */
  const login = useCallback(
    ({ token, refreshToken, user, csrfToken }) => {
      saveAccessToken(token || null); // ✅ Guarda access token en sessionStorage
      saveRefreshToken(refreshToken || null); // ✅ Guarda refresh token como fallback
      setUser(user || null);
      saveCsrf(csrfToken || null); // ✅ guarda el CSRF real
    },
    [saveAccessToken, saveRefreshToken, saveCsrf],
  );

  /**
   * ✅ logout: NO CSRF
   */
  const logout = useCallback(async () => {
    try {
      const body = {};

      try {
        const refreshTokenFromStorage = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        if (refreshTokenFromStorage) {
          body.refreshToken = refreshTokenFromStorage;
        }
      } catch {
        // ignore storage errors
      }

      await fetch(`${API}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "y-device-id": getDeviceId(),
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });
    } finally {
      saveAccessToken(null); // ✅ Limpia sessionStorage
      saveRefreshToken(null); // ✅ Limpia refresh token fallback
      setUser(null);
      saveCsrf(null);
      clearPendingTask();
    }
  }, [saveAccessToken, saveRefreshToken, saveCsrf, clearPendingTask]);

  // mantiene el ref actualizado en cada render
  logoutRef.current = logout;

  /**
   * ✅ ensureAuth:
   */
  const ensureAuth = useCallback(async () => {
    if (accessToken) return accessToken;
    return await refresh();
  }, [accessToken, refresh]);

  /**
   * ✅ authFetch:
   */
  const authFetch = useCallback(
    async (path, options = {}) => {
      const url = path.startsWith("http") ? path : `${API}${path}`;
      const method = (options.method || "GET").toUpperCase();

      const token = await ensureAuth();

      const headers = {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const needsCsrf = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
      if (needsCsrf && csrfToken) headers["y-csrf-token"] = csrfToken;

      const firstResp = await fetch(url, {
        ...options,
        credentials: "include",
        headers,
      });

      if (firstResp.status !== 401) return firstResp;

      const newToken = await refresh();
      if (!newToken) return firstResp;

      // importante: usar el csrfToken actual (puede haber cambiado con refresh)
      const csrfNow = (() => {
        try {
          return sessionStorage.getItem(CSRF_STORAGE_KEY);
        } catch {
          return csrfToken;
        }
      })();

      const retryHeaders = {
        ...(options.headers || {}),
        Authorization: `Bearer ${newToken}`,
      };
      if (needsCsrf && csrfNow) retryHeaders["y-csrf-token"] = csrfNow;

      return fetch(url, {
        ...options,
        credentials: "include",
        headers: retryHeaders,
      });
    },
    [ensureAuth, refresh, csrfToken],
  );

  // Track one pending task globally so processing can continue after route changes/refresh.
  useEffect(() => {
    if (bootstrapping) return;
    if (!pendingTask?.id) return;

    let cancelled = false;

    const dispatchEvent = (name, detail) => {
      try {
        window.dispatchEvent(new CustomEvent(name, { detail }));
      } catch {
        // ignore event dispatch errors
      }
    };

    const pollAndFinalize = async () => {
      try {
        const statusResp = await authFetch(
          `/api/sendtask/tasktodo/${pendingTask.id}/status`,
          { method: "GET" },
        );

        const statusData = await statusResp.json().catch(() => ({}));
        if (cancelled) return;

        if (statusResp.status === 404) {
          clearPendingTask();
          dispatchEvent("scenathon-task-failed", {
            taskId: pendingTask.id,
            code: "TASK_NOT_FOUND",
            error: "Task not found",
            pendingTask,
          });
          return;
        }

        if (!statusResp.ok) {
          setPendingTaskState("error");
          return;
        }

        const currentStatus = String(statusData?.status || "").toLowerCase();
        setPendingTaskState(currentStatus || "unknown");

        if (currentStatus !== "done") return;

        const completeResp = await authFetch(
          `/api/sendtask/tasktodo/${pendingTask.id}/complete?waitForDone=false`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              description: null,
              country: pendingTask.country || null,
              overwrite: !!pendingTask.overwrite,
            }),
          },
        );

        const completeData = await completeResp.json().catch(() => ({}));
        if (cancelled) return;

        if (completeResp.ok) {
          clearPendingTask();
          setPendingTaskState("completed");
          dispatchEvent("scenathon-task-completed", {
            taskId: pendingTask.id,
            pendingTask,
            ...completeData,
          });
          return;
        }

        if (completeData?.code === "TASK_NOT_DONE") {
          return;
        }

        clearPendingTask();
        setPendingTaskState("failed");
        dispatchEvent("scenathon-task-failed", {
          taskId: pendingTask.id,
          pendingTask,
          code: completeData?.code || "COMPLETE_FAILED",
          error: completeData?.error || `Error ${completeResp.status}`,
        });
      } catch {
        if (!cancelled) setPendingTaskState("error");
      }
    };

    void pollAndFinalize();
    const intervalId = setInterval(() => {
      void pollAndFinalize();
    }, 4000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [authFetch, bootstrapping, pendingTask, clearPendingTask]);

  const value = useMemo(
    () => ({
      accessToken,
      user,
      csrfToken,
      bootstrapping,
      login,
      logout,
      ensureAuth,
      authFetch,
      pendingTask,
      pendingTaskState,
      startPendingTask,
      clearPendingTask,
      setAccessToken: saveAccessToken,
      setUser,
      setCsrfToken: saveCsrf,
    }),
    [
      accessToken,
      user,
      csrfToken,
      bootstrapping,
      login,
      logout,
      ensureAuth,
      authFetch,
      pendingTask,
      pendingTaskState,
      startPendingTask,
      clearPendingTask,
      saveAccessToken,
      saveRefreshToken,
      saveCsrf,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider />");
  return ctx;
}
